import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import {
  InvoiceDisplaySettings,
  DEFAULT_INVOICE_DISPLAY_SETTINGS,
} from '../../models/invoice-display-settings';

@Component({
  selector: 'app-invoice-display-settings-dialog',
  templateUrl: './invoice-display-settings-dialog.component.html',
  styles: [`
    :host {
      display: block;
      font-family: 'Manrope', sans-serif;
    }

    .form-dialog {
      padding: 24px;
      max-width: 100%;
      overflow: hidden;
      font-family: 'Manrope', sans-serif;
    }

    /* Material renders dialog content with its own (Roboto) typography;
       force the app typeface on everything inside, incl. checkbox labels. */
    :host ::ng-deep .form-dialog,
    :host ::ng-deep .form-dialog .mat-checkbox-label,
    :host ::ng-deep .form-dialog .mat-dialog-title {
      font-family: 'Manrope', sans-serif !important;
    }

    .dialog-title {
      color: #1C1C1C;
      font-size: 18px;
      font-weight: 700;
      margin: 0 0 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 16px;
      border-bottom: 1px solid #E8E8E8;
    }

    .close-icon {
      color: #5D5D5D;
      cursor: pointer;
      padding: 8px;
      margin: -8px;
      transition: color 0.2s;
    }

    .close-icon:hover { color: #1C1C1C; }

    .dialog-content {
      margin: 0;
      padding: 0;
      overflow: visible;
    }

    .section-label {
      font-size: 13px;
      font-weight: 700;
      color: #4F4F4F;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 20px 0 10px;
    }

    .hint {
      font-size: 12px;
      color: #777777;
      margin: 4px 0 12px;
    }

    .setting-row {
      display: flex;
      align-items: center;
      padding: 8px 0;
    }

    .setting-row .mat-checkbox { font-size: 14px; }

    .radio-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      cursor: pointer;
      font-size: 14px;
      color: #1C1C1C;
    }

    .radio-row input[type='radio'] { accent-color: #FFC93B; }

    .dialog-actions {
      margin: 28px 0 0;
      padding: 16px 0 0;
      border-top: 1px solid #E8E8E8;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    /* Buttons reuse the app-wide custom_button classes (assets/css/style.css);
       only sizing tweaks and a disabled state are needed here. */
    .dialog-actions .custom_button {
      padding: 8px 20px;
      font-size: 14px;
      min-width: 100px;
      cursor: pointer;
    }

    .dialog-actions .custom_button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `]
})
export class InvoiceDisplaySettingsDialogComponent {
  settings: InvoiceDisplaySettings;
  isXero: boolean;
  isQuickBook: boolean;
  organisationId: string;
  saving = false;

  constructor(
    private dialogRef: MatDialogRef<InvoiceDisplaySettingsDialogComponent>,
    private _organizationService: OrganizationService,
    private _toasterMessageService: ToasterMessageService,
    @Inject(MAT_DIALOG_DATA) public data: {
      settings: InvoiceDisplaySettings;
      isXero: boolean;
      isQuickBook: boolean;
      organisationId: string;
    }
  ) {
    this.isXero = !!data.isXero;
    this.isQuickBook = !!data.isQuickBook;
    this.organisationId = data.organisationId;
    // Work on a deep copy so Cancel leaves the original untouched.
    const source = data.settings || DEFAULT_INVOICE_DISPLAY_SETTINGS;
    this.settings = {
      fields: { ...DEFAULT_INVOICE_DISPLAY_SETTINGS.fields, ...source.fields },
      accountDisplay: source.accountDisplay || DEFAULT_INVOICE_DISPLAY_SETTINGS.accountDisplay,
    };
  }

  onSave() {
    this.saving = true;
    this._organizationService
      .updateInvoiceDisplaySettings({
        organisationId: this.organisationId,
        settings: this.settings,
      })
      .subscribe(
        () => {
          this.saving = false;
          this.dialogRef.close(this.settings);
        },
        () => {
          this.saving = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            'Unable to save display settings. Please try again.'
          );
        }
      );
  }

  onCancel() {
    this.dialogRef.close();
  }
}
